import React, { useState, useEffect, useRef } from "react";
import {
  FaFileExcel,
  FaCogs,
  FaCheckCircle,
  FaMoneyCheckAlt,
  FaCalendarAlt,
  FaReceipt,
  FaChevronDown,
  FaChevronUp,
  FaTrashAlt,
  FaSyncAlt,
  FaEye,
  FaTimes,
  FaPlus,
  FaUserTie,
  FaCheck,
  FaSearch,
  FaSpinner,
} from "react-icons/fa";
import apiRequest from "../services/api";
import downloadPayrollExcel from "../utils/downloadPayrollExcel";

const PayrollManagement = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [payrolls, setPayrolls] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [expandedPayrollId, setExpandedPayrollId] = useState(null);
  const [generationEnabled, setGenerationEnabled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPayrollDetails, setSelectedPayrollDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const [currentUser, setCurrentUser] = useState(() => {
    const savedUser = localStorage.getItem("user");
    try {
      return savedUser ? JSON.parse(savedUser) : null;
    } catch (e) {
      return null;
    }
  });

  const [params, setParams] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    employeeIds: [],
  });

  const [paymentMethod, setPaymentMethod] = useState("BANK");
  const [bankCommission, setBankCommission] = useState(0.0);
  const dropdownRef = useRef(null);

  useEffect(() => {
    fetchPayrolls();
    fetchEmployees();

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchEmployees = async () => {
    try {
      const resp = await apiRequest("employees", { method: "GET" });
      const dataArray = resp.data?.content || resp.data || resp;
      setEmployees(Array.isArray(dataArray) ? dataArray : []);
    } catch (err) {
      console.warn("Failed to load employees:", err);
    }
  };

  const fetchPayrolls = async () => {
    setLoading(true);
    setError("");
    try {
      const resp = await apiRequest("payrolls", { method: "GET" });
      // API returns { success, message, data: [...] }
      setPayrolls(resp.data || []);
    } catch (err) {
      setError(err.message || "Failed to load payrolls.");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleEmployee = (id) => {
    setParams((prev) => ({
      ...prev,
      employeeIds: prev.employeeIds.includes(id)
        ? prev.employeeIds.filter((employeeId) => employeeId !== id)
        : [...prev.employeeIds, id],
    }));
  };

  const handleClearEmployees = () => {
    setParams((prev) => ({ ...prev, employeeIds: [] }));
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccessMessage("");

    try {
      let url = `payrolls/generate?month=${params.month}&year=${params.year}`;
      if (params.employeeIds.length > 0) {
        url += `&employeeIds=${params.employeeIds.join(",")}`;
      }

      const resp = await apiRequest(url, { method: "POST" });
      setSuccessMessage(
        resp.message || "Payroll summary processing executed successfully."
      );
      fetchPayrolls();
    } catch (err) {
      setError(err.message || "Failed to generate payroll.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Are you sure you want to delete this payroll?"))
      return;

    setLoading(true);
    setError("");
    setSuccessMessage("");

    try {
      await apiRequest(`payrolls/${id}`, { method: "DELETE" });
      setSuccessMessage("Payroll deleted successfully.");
      if (expandedPayrollId === id) setExpandedPayrollId(null);
      fetchPayrolls();
    } catch (err) {
      setError(err.message || "Failed to delete payroll.");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    if (
      !window.confirm(
        "Are you sure you want to permanently approve this payroll?"
      )
    ) {
      return;
    }

    if (
      paymentMethod === "BANK" &&
      (!bankCommission || Number(bankCommission) <= 0)
    ) {
      alert("Bank commission is required for bank payment.");
      return;
    }

    setLoading(true);
    setError("");
    setSuccessMessage("");

    try {
      await apiRequest(
        `payrolls/${id}/approve?method=${paymentMethod}&bankCommission=${bankCommission}`,
        {
          method: "POST",
        }
      );
      setSuccessMessage("Payroll approved successfully.");
      fetchPayrolls();
    } catch (err) {
      setError(err.message || "Failed to approve payroll.");
    } finally {
      setLoading(false);
    }
  };

  const fetchItemPackageDetails = async (itemId) => {
    setLoadingDetails(true);
    try {
      const resp = await apiRequest(`payrolls/employee-packages/${itemId}`, {
        method: "GET",
      });
      setSelectedPayrollDetails(resp.data || []);
    } catch (err) {
      setError(err.message || "Failed to load package details.");
    } finally {
      setLoadingDetails(false);
    }
  };

  const toggleAccordion = (id) => {
    setExpandedPayrollId((prev) => (prev === id ? null : id));
  };

  const filteredEmployees = employees.filter((emp) => {
    if (!searchTerm.trim()) return true;

    const term = searchTerm.toLowerCase().trim();

    const fullName = [
      emp.firstName,
      emp.lastName,
      emp.name,
      emp.fullName,
      emp.employeeName,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    const idMatch = emp.id?.toString().includes(term);
    const nameMatch = fullName.includes(term);
    const emailMatch = (emp.email || "").toLowerCase().includes(term);
    const phoneMatch = (emp.phone || emp.phoneNumber || "")
      .toString()
      .includes(term);

    return nameMatch || idMatch || emailMatch || phoneMatch;
  });

  const formatAmount = (amount) =>
    Number(amount || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const monthName = (monthNum) => {
    const names = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return names[(monthNum || 1) - 1] || "";
  };

  const labelClass =
    "block text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2";

  const fieldClass =
    "w-full rounded-xl border bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 dark:bg-slate-950/60 dark:border-white/10 dark:text-white dark:placeholder-slate-600 px-3 py-2.5 transition";

  return (
    <div className="min-h-full p-3 sm:p-4 md:p-8 font-sans bg-slate-50 text-slate-800 dark:bg-slate-950 dark:text-slate-100">
      <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="rounded-2xl border bg-white border-slate-200 shadow-sm dark:bg-slate-900/40 dark:border-white/10 dark:shadow-xl p-4 sm:p-5 md:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-2xl flex items-center justify-center border bg-gradient-to-br from-emerald-100 to-teal-50 border-emerald-200 text-emerald-600 dark:from-emerald-500/20 dark:to-teal-500/10 dark:border-emerald-500/30 dark:text-emerald-400">
                <FaMoneyCheckAlt className="text-lg sm:text-xl" />
              </div>
              <div>
                <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-slate-900 dark:text-white">
                  Payroll Management
                </h1>
                <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                  Generate, review and approve employee payroll
                </p>
              </div>
            </div>

            <button
              onClick={fetchPayrolls}
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold border bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100 dark:bg-slate-950/60 dark:border-white/10 dark:text-slate-300 dark:hover:bg-white/[0.04] transition w-full sm:w-auto"
            >
              <FaSyncAlt className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300 px-4 py-3 text-sm">
            {error}
          </div>
        )}
        {successMessage && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300 px-4 py-3 text-sm flex items-center gap-2">
            <FaCheckCircle />
            {successMessage}
          </div>
        )}

        {/* Main */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-6">
          {/* Generation Card */}
          <div className="lg:col-span-1">
            <div className="rounded-2xl border bg-white border-slate-200 shadow-sm dark:bg-slate-900/30 dark:border-white/10 dark:shadow-2xl overflow-hidden lg:sticky lg:top-6">
              <div className="p-4 sm:p-5 border-b border-slate-200 dark:border-white/10">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-emerald-50 text-emerald-600 border border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20">
                    <FaCogs />
                  </div>
                  <div>
                    <h2 className="font-bold text-slate-900 dark:text-white">
                      Generate Payroll
                    </h2>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Process employee salaries
                    </p>
                  </div>
                </div>
              </div>

              <form
                onSubmit={handleGenerate}
                className="p-4 sm:p-5 space-y-4 sm:space-y-5"
              >
                {/* Month */}
                <div>
                  <label className={labelClass}>
                    <FaCalendarAlt className="inline mr-1" />
                    Month
                  </label>
                  <select
                    value={params.month}
                    onChange={(e) =>
                      setParams({ ...params, month: Number(e.target.value) })
                    }
                    className={fieldClass}
                  >
                    {[
                      "January",
                      "February",
                      "March",
                      "April",
                      "May",
                      "June",
                      "July",
                      "August",
                      "September",
                      "October",
                      "November",
                      "December",
                    ].map((month, index) => (
                      <option key={index + 1} value={index + 1}>
                        {month}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Year */}
                <div>
                  <label className={labelClass}>Year</label>
                  <input
                    type="number"
                    value={params.year}
                    disabled
                    className={`${fieldClass} opacity-70 cursor-not-allowed`}
                  />
                </div>

                {/* Employees */}
                <div ref={dropdownRef} className="relative">
                  <label className={labelClass}>
                    <FaUserTie className="inline mr-1" />
                    Employees
                  </label>
                  <button
                    type="button"
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    className={`${fieldClass} flex items-center justify-between text-left`}
                  >
                    <span
                      className={
                        params.employeeIds.length
                          ? "text-slate-800 dark:text-white"
                          : "text-slate-400 dark:text-slate-600"
                      }
                    >
                      {params.employeeIds.length
                        ? `${params.employeeIds.length} employee${
                            params.employeeIds.length > 1 ? "s" : ""
                          } selected`
                        : "All employees"}
                    </span>
                    {dropdownOpen ? <FaChevronUp /> : <FaChevronDown />}
                  </button>

                  {dropdownOpen && (
                    <div className="absolute z-30 mt-2 w-full rounded-2xl border bg-white border-slate-200 shadow-xl dark:bg-slate-900 dark:border-white/10 dark:shadow-2xl overflow-hidden">
                      <div className="p-3 border-b bg-slate-50 border-slate-200 dark:bg-slate-950/70 dark:border-white/10">
                        <div className="relative">
                          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-600 text-xs" />
                          <input
                            type="text"
                            placeholder="Search employees..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                            className="w-full pl-9 pr-3 py-2 rounded-lg border bg-white border-slate-200 text-slate-800 placeholder-slate-400 focus:outline-none focus:border-emerald-500 dark:bg-slate-900 dark:border-white/10 dark:text-white dark:placeholder-slate-600"
                          />
                        </div>
                        {params.employeeIds.length > 0 && (
                          <button
                            type="button"
                            onClick={handleClearEmployees}
                            className="mt-2 text-xs font-medium text-red-600 dark:text-red-400 hover:underline"
                          >
                            Clear selection
                          </button>
                        )}
                      </div>

                      <div className="max-h-60 overflow-y-auto">
                        {filteredEmployees.length > 0 ? (
                          filteredEmployees.map((emp) => {
                            const selected = params.employeeIds.includes(
                              emp.id
                            );
                            const displayName =
                              emp.fullName ||
                              emp.employeeName ||
                              emp.name ||
                              [emp.firstName, emp.lastName]
                                .filter(Boolean)
                                .join(" ") ||
                              `Employee #${emp.id}`;

                            return (
                              <button
                                key={emp.id}
                                type="button"
                                onClick={() => handleToggleEmployee(emp.id)}
                                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition ${
                                  selected
                                    ? "bg-emerald-50 dark:bg-emerald-500/10"
                                    : "hover:bg-slate-50 dark:hover:bg-white/[0.03]"
                                }`}
                              >
                                <div
                                  className={`w-5 h-5 rounded-md border flex items-center justify-center shrink-0 ${
                                    selected
                                      ? "bg-emerald-500 border-emerald-500 text-white"
                                      : "border-slate-300 dark:border-white/20"
                                  }`}
                                >
                                  {selected && <FaCheck className="text-xs" />}
                                </div>

                                <div className="min-w-0">
                                  <div className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">
                                    {displayName}
                                  </div>
                                  <div className="text-xs text-slate-400 dark:text-slate-500">
                                    ID: {emp.id}
                                  </div>
                                </div>
                              </button>
                            );
                          })
                        ) : (
                          <div className="p-5 text-center text-sm text-slate-400">
                            {searchTerm
                              ? "No employees match your search"
                              : "No employees found"}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Safety checkbox */}
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={generationEnabled}
                    onChange={(e) => setGenerationEnabled(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 dark:border-white/20 dark:bg-slate-950"
                  />
                  <span>
                    <span className="block text-sm font-semibold text-slate-800 dark:text-slate-200">
                      Run new payroll
                    </span>
                    <span className="block text-xs text-slate-400 dark:text-slate-500 mt-1">
                      Enable this before processing payroll.
                    </span>
                  </span>
                </label>

                <button
                  type="submit"
                  disabled={loading || !generationEnabled}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition shadow-sm"
                >
                  {loading ? (
                    <FaSpinner className="animate-spin" />
                  ) : (
                    <FaPlus />
                  )}
                  Generate Payroll
                </button>
              </form>
            </div>
          </div>

          {/* Payroll List */}
          <div className="lg:col-span-3 space-y-4">
            {loading && payrolls.length === 0 ? (
              <div className="rounded-2xl border bg-white border-slate-200 shadow-sm dark:bg-slate-900/30 dark:border-white/10 p-12 text-center">
                <FaSpinner className="mx-auto text-2xl text-emerald-500 animate-spin mb-3" />
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Loading payrolls...
                </p>
              </div>
            ) : payrolls.length === 0 ? (
              <div className="rounded-2xl border bg-white border-slate-200 shadow-sm dark:bg-slate-900/30 dark:border-white/10 p-12 text-center">
                <FaReceipt className="mx-auto text-3xl text-slate-300 dark:text-slate-700 mb-4" />
                <h3 className="font-semibold text-slate-700 dark:text-slate-300">
                  No payrolls found
                </h3>
                <p className="text-sm text-slate-400 dark:text-slate-500 mt-1">
                  Generate a payroll to get started.
                </p>
              </div>
            ) : (
              payrolls.map((payroll) => {
                const expanded = expandedPayrollId === payroll.id;

                return (
                  <div
                    key={payroll.id}
                    className="rounded-2xl border bg-white border-slate-200 shadow-sm dark:bg-slate-900/30 dark:border-white/10 dark:shadow-2xl overflow-hidden"
                  >
                    {/* Payroll Header */}
                    <div
                      onClick={() => toggleAccordion(payroll.id)}
                      className="p-4 sm:p-5 cursor-pointer hover:bg-slate-50 dark:hover:bg-white/[0.02] transition"
                    >
                      <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-3 sm:gap-4">
                        <div className="flex items-start gap-3 sm:gap-4 min-w-0">
                          <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl flex-shrink-0 flex items-center justify-center bg-slate-100 text-slate-500 border border-slate-200 dark:bg-slate-950/60 dark:border-white/10 dark:text-slate-400">
                            <FaMoneyCheckAlt />
                          </div>

                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <h3 className="font-bold text-slate-900 dark:text-white text-sm sm:text-base">
                                {payroll.description ||
                                  `${monthName(payroll.payrollMonth)} ${
                                    payroll.payrollYear
                                  } Payroll`}
                              </h3>
                              <span
                                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] sm:text-xs font-semibold border ${
                                  payroll.status === "PAID"
                                    ? "bg-cyan-50 text-cyan-700 border-cyan-200 dark:bg-cyan-500/10 dark:text-cyan-400 dark:border-cyan-500/20"
                                    : "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/20"
                                }`}
                              >
                                {payroll.status === "PAID" && <FaCheckCircle />}
                                {payroll.status || "DRAFT"}
                              </span>
                            </div>

                            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-[11px] sm:text-xs text-slate-400 dark:text-slate-500">
                              {payroll.branchName && (
                                <span>Branch: {payroll.branchName}</span>
                              )}
                              {payroll.payrollDate && (
                                <span>Date: {payroll.payrollDate}</span>
                              )}
                              {payroll.paymentMethod && (
                                <span>Method: {payroll.paymentMethod}</span>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center justify-between xl:justify-end gap-3 sm:gap-5 pl-13 sm:pl-0">
                          <div className="text-right">
                            <div className="text-[10px] sm:text-xs uppercase tracking-wider text-slate-400 dark:text-slate-500">
                              Net Salary
                            </div>
                            <div className="text-base sm:text-lg font-bold text-emerald-600 dark:text-emerald-400">
                              ETB {formatAmount(payroll.totalNetSalary)}
                            </div>
                          </div>

                          {/* ===== Excel Export Button ===== */}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation(); // prevent accordion toggle
                              downloadPayrollExcel(
                                payroll,
                                currentUser.data?.tenant?.name ||
                                  "Muyaz technologies"
                              );
                            }}
                            className="w-9 h-9 rounded-lg flex items-center justify-center
          text-emerald-600 hover:bg-emerald-50 border border-transparent
          hover:border-emerald-200
          dark:text-emerald-400 dark:hover:bg-emerald-500/10 dark:hover:border-emerald-500/20
          transition"
                            title="Download Excel"
                          >
                            <FaFileExcel className="text-sm" />
                          </button>

                          {payroll.status === "DRAFT" && (
                            <button
                              onClick={(e) => handleDelete(payroll.id, e)}
                              className="w-9 h-9 rounded-lg flex items-center justify-center text-red-500 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10 transition"
                              title="Delete payroll"
                            >
                              <FaTrashAlt />
                            </button>
                          )}

                          <div className="text-slate-400 dark:text-slate-500">
                            {expanded ? <FaChevronUp /> : <FaChevronDown />}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {expanded && (
                      <div className="border-t border-slate-200 dark:border-white/10">
                        {/* Summary cards – mapped to API fields */}
                        <div className="p-4 sm:p-5 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2 sm:gap-3">
                          <div className="rounded-xl border bg-slate-50 border-slate-200 p-3 sm:p-4 dark:bg-slate-950/40 dark:border-white/10">
                            <div className="text-[10px] sm:text-xs text-slate-400 dark:text-slate-500">
                              Gross Payroll
                            </div>
                            <div className="mt-1 font-bold text-sm sm:text-base text-slate-800 dark:text-slate-200">
                              ETB {formatAmount(payroll.totalGrossSalary)}
                            </div>
                          </div>

                          <div className="rounded-xl border bg-rose-50 border-rose-100 p-3 sm:p-4 dark:bg-rose-500/5 dark:border-rose-500/10">
                            <div className="text-[10px] sm:text-xs text-rose-500 dark:text-rose-400">
                              PAYE Tax
                            </div>
                            <div className="mt-1 font-bold text-sm sm:text-base text-rose-700 dark:text-rose-300">
                              ETB {formatAmount(payroll.totalTax)}
                            </div>
                          </div>

                          <div className="rounded-xl border bg-amber-50 border-amber-100 p-3 sm:p-4 dark:bg-amber-500/5 dark:border-amber-500/10">
                            <div className="text-[10px] sm:text-xs text-amber-600 dark:text-amber-400">
                              Employee Pension
                            </div>
                            <div className="mt-1 font-bold text-sm sm:text-base text-amber-700 dark:text-amber-300">
                              ETB {formatAmount(payroll.totalEmployeePension)}
                            </div>
                          </div>

                          <div className="rounded-xl border bg-indigo-50 border-indigo-100 p-3 sm:p-4 dark:bg-indigo-500/5 dark:border-indigo-500/10">
                            <div className="text-[10px] sm:text-xs text-indigo-600 dark:text-indigo-400">
                              Employer Pension
                            </div>
                            <div className="mt-1 font-bold text-sm sm:text-base text-indigo-700 dark:text-indigo-300">
                              ETB {formatAmount(payroll.totalEmployerPension)}
                            </div>
                          </div>

                          <div className="rounded-xl border bg-red-50 border-red-100 p-3 sm:p-4 dark:bg-red-500/5 dark:border-red-500/10">
                            <div className="text-[10px] sm:text-xs text-red-500 dark:text-red-400">
                              Advance Ded.
                            </div>
                            <div className="mt-1 font-bold text-sm sm:text-base text-red-700 dark:text-red-300">
                              ETB {formatAmount(payroll.advanceDeduction)}
                            </div>
                          </div>

                          <div className="rounded-xl border bg-orange-50 border-orange-100 p-3 sm:p-4 dark:bg-orange-500/5 dark:border-orange-500/10">
                            <div className="text-[10px] sm:text-xs text-orange-600 dark:text-orange-400">
                              Other Ded.
                            </div>
                            <div className="mt-1 font-bold text-sm sm:text-base text-orange-700 dark:text-orange-300">
                              ETB {formatAmount(payroll.totalDeductions)}
                            </div>
                          </div>
                        </div>

                        {/* Payroll Items – desktop table */}
                        <div className="px-4 sm:px-5 pb-4 sm:pb-5">
                          <div className="rounded-xl border border-slate-200 dark:border-white/10 overflow-hidden">
                            {/* Mobile cards */}
                            <div className="md:hidden divide-y divide-slate-100 dark:divide-white/5">
                              {(payroll.items || []).map((item) => (
                                <div key={item.id} className="p-4 space-y-2">
                                  <div className="flex justify-between items-start gap-2">
                                    <div className="font-medium text-slate-800 dark:text-slate-200 text-sm">
                                      {item.employeeName || "—"}
                                    </div>
                                    <button
                                      onClick={() =>
                                        fetchItemPackageDetails(item.id)
                                      }
                                      className="p-2 rounded-lg text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 dark:text-slate-400 dark:hover:text-emerald-400 dark:hover:bg-emerald-500/10"
                                    >
                                      <FaEye />
                                    </button>
                                  </div>
                                  <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
                                    <span className="text-slate-400">
                                      Gross
                                    </span>
                                    <span className="text-right text-slate-700 dark:text-slate-300">
                                      ETB {formatAmount(item.grossSalary)}
                                    </span>
                                    <span className="text-slate-400">Tax</span>
                                    <span className="text-right text-rose-600 dark:text-rose-400">
                                      ETB {formatAmount(item.taxAmount)}
                                    </span>
                                    <span className="text-slate-400">
                                      Pension (EE)
                                    </span>
                                    <span className="text-right text-amber-600 dark:text-amber-400">
                                      ETB {formatAmount(item.employeePension)}
                                    </span>
                                    <span className="text-slate-400">
                                      Advance
                                    </span>
                                    <span className="text-right text-red-600 dark:text-red-400">
                                      ETB {formatAmount(item.advanceDeduction)}
                                    </span>
                                    <span className="text-slate-400">
                                      Other Ded.
                                    </span>
                                    <span className="text-right text-red-600 dark:text-red-400">
                                      ETB {formatAmount(item.totalDeductions)}
                                    </span>
                                    <span className="text-slate-400 font-semibold">
                                      Net
                                    </span>
                                    <span className="text-right font-bold text-emerald-600 dark:text-emerald-400">
                                      ETB {formatAmount(item.netSalary)}
                                    </span>
                                  </div>
                                </div>
                              ))}
                              {(!payroll.items ||
                                payroll.items.length === 0) && (
                                <div className="p-6 text-center text-slate-400 text-sm">
                                  No payroll items found.
                                </div>
                              )}
                            </div>

                            {/* Desktop table */}
                            <div className="hidden md:block overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 dark:bg-slate-950/50 dark:border-white/10 dark:text-slate-400">
                                    <th className="px-4 py-3 text-left font-semibold">
                                      Name
                                    </th>
                                    <th className="px-4 py-3 text-right font-semibold">
                                      Gross
                                    </th>
                                    <th className="px-4 py-3 text-right font-semibold">
                                      Tax Ded.
                                    </th>
                                    <th className="px-4 py-3 text-right font-semibold">
                                      Pension (EE)
                                    </th>
                                    <th className="px-4 py-3 text-right font-semibold">
                                      Advance Ded.
                                    </th>
                                    <th className="px-4 py-3 text-right font-semibold">
                                      Other Ded.
                                    </th>
                                    <th className="px-4 py-3 text-right font-semibold">
                                      Net Payable
                                    </th>
                                    <th className="px-4 py-3 text-center font-semibold">
                                      Details
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-white/5">
                                  {(payroll.items || []).map((item) => (
                                    <tr
                                      key={item.id}
                                      className="hover:bg-slate-50 dark:hover:bg-white/[0.02] transition"
                                    >
                                      <td className="px-4 py-3">
                                        <div className="font-medium text-slate-800 dark:text-slate-200">
                                          {item.employeeName || "—"}
                                        </div>
                                      </td>
                                      <td className="px-4 py-3 text-right text-slate-700 dark:text-slate-300">
                                        ETB {formatAmount(item.grossSalary)}
                                      </td>
                                      <td className="px-4 py-3 text-right text-rose-600 dark:text-rose-400">
                                        ETB {formatAmount(item.taxAmount)}
                                      </td>
                                      <td className="px-4 py-3 text-right text-amber-600 dark:text-amber-400">
                                        ETB {formatAmount(item.employeePension)}
                                      </td>
                                      <td className="px-4 py-3 text-right text-red-600 dark:text-red-400">
                                        ETB{" "}
                                        {formatAmount(item.advanceDeduction)}
                                      </td>
                                      <td className="px-4 py-3 text-right text-red-600 dark:text-red-400">
                                        ETB {formatAmount(item.totalDeductions)}
                                      </td>
                                      <td className="px-4 py-3 text-right font-bold text-emerald-600 dark:text-emerald-400">
                                        ETB {formatAmount(item.netSalary)}
                                      </td>
                                      <td className="px-4 py-3 text-center">
                                        <button
                                          onClick={() =>
                                            fetchItemPackageDetails(item.id)
                                          }
                                          className="inline-flex items-center justify-center w-9 h-9 rounded-lg text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 dark:text-slate-400 dark:hover:text-emerald-400 dark:hover:bg-emerald-500/10 transition"
                                          title="View package details"
                                        >
                                          <FaEye />
                                        </button>
                                      </td>
                                    </tr>
                                  ))}
                                  {(!payroll.items ||
                                    payroll.items.length === 0) && (
                                    <tr>
                                      <td
                                        colSpan="8"
                                        className="px-4 py-8 text-center text-slate-400 dark:text-slate-500"
                                      >
                                        No payroll items found.
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                        {/* Approval */}
                        {payroll.status === "DRAFT" && (
                          <div className="mx-4 sm:mx-5 mb-4 sm:mb-5 rounded-xl border border-amber-200 bg-amber-50 p-4 sm:p-5 dark:border-amber-500/20 dark:bg-amber-500/5">
                            <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
                              {/* Description */}
                              <div className="min-w-0">
                                <h4 className="font-semibold text-slate-800 dark:text-slate-200">
                                  Approve Payroll
                                </h4>
                                <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
                                  Approval will permanently post the transaction
                                  to accounting.
                                </p>
                              </div>

                              {/* Payment Controls */}
                              <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 lg:w-auto lg:grid-cols-[150px_170px_auto] lg:items-end">
                                {/* Payment Method */}
                                <div>
                                  <label className="mb-1.5 block text-xs font-medium text-slate-600 dark:text-slate-300">
                                    Payment Method
                                  </label>
                                  <select
                                    value={paymentMethod}
                                    onChange={(e) => {
                                      setPaymentMethod(e.target.value);
                                      if (e.target.value !== "BANK") {
                                        setBankCommission("");
                                      }
                                    }}
                                    className={`${fieldClass} w-full`}
                                  >
                                    <option value="BANK">BANK</option>
                                    <option value="CASH">CASH</option>
                                  </select>
                                </div>

                                {/* Bank Commission */}
                                <div>
                                  <label className="mb-1.5 block text-xs font-medium text-slate-600 dark:text-slate-300">
                                    Bank Commission
                                    {paymentMethod === "BANK" && (
                                      <span className="ml-1 text-red-500">
                                        *
                                      </span>
                                    )}
                                  </label>
                                  <input
                                    type="number"
                                    min="0.01"
                                    step="0.01"
                                    placeholder={
                                      paymentMethod === "BANK"
                                        ? "Required"
                                        : "0.00"
                                    }
                                    value={bankCommission}
                                    onChange={(e) =>
                                      setBankCommission(e.target.value)
                                    }
                                    disabled={paymentMethod !== "BANK"}
                                    required={paymentMethod === "BANK"}
                                    className={`${fieldClass} w-full disabled:cursor-not-allowed disabled:opacity-50`}
                                  />
                                </div>

                                {/* Approve Button */}
                                <button
                                  onClick={() => handleApprove(payroll.id)}
                                  disabled={loading}
                                  className="rounded-xl bg-emerald-600 px-5 font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50 sm:col-span-2 lg:col-span-1"
                                >
                                  {loading && (
                                    <FaSpinner className="animate-spin" />
                                  )}
                                  Pay & Post
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Loading Details overlay */}
        {loadingDetails && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 dark:bg-slate-950/70 backdrop-blur-sm">
            <div className="rounded-2xl border bg-white border-slate-200 shadow-2xl dark:bg-slate-900 dark:border-white/10 px-8 py-7 text-center">
              <FaSpinner className="mx-auto text-2xl text-emerald-500 animate-spin mb-3" />
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                Loading package details...
              </p>
            </div>
          </div>
        )}

        {/* Package Details Modal */}
        {selectedPayrollDetails && selectedPayrollDetails.length > 0 && (
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-slate-900/40 dark:bg-slate-950/70 backdrop-blur-sm">
            <div className="w-full max-w-4xl max-h-[92vh] sm:max-h-[90vh] overflow-hidden rounded-t-2xl sm:rounded-2xl border bg-white border-slate-200 shadow-2xl dark:bg-slate-900 dark:border-white/10">
              <div className="flex items-center justify-between p-4 sm:p-5 border-b border-slate-200 dark:border-white/10">
                <div>
                  <h2 className="text-base sm:text-lg font-bold text-slate-900 dark:text-white">
                    Employee Package Details
                  </h2>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    Salary package components
                  </p>
                </div>
                <button
                  onClick={() => setSelectedPayrollDetails(null)}
                  className="w-9 h-9 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-white/[0.05] transition"
                >
                  <FaTimes />
                </button>
              </div>

              <div className="overflow-auto max-h-[65vh] sm:max-h-[70vh]">
                <table className="w-full text-sm">
                  <thead className="sticky top-0">
                    <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 dark:bg-slate-950/70 dark:border-white/10 dark:text-slate-400">
                      <th className="px-4 sm:px-5 py-3 text-left font-semibold">
                        Package Name
                      </th>
                      <th className="px-4 sm:px-5 py-3 text-left font-semibold">
                        Type
                      </th>
                      <th className="px-4 sm:px-5 py-3 text-right font-semibold">
                        Amount
                      </th>
                      <th className="px-4 sm:px-5 py-3 text-right font-semibold">
                        Taxable
                      </th>
                      <th className="px-4 sm:px-5 py-3 text-right font-semibold">
                        Pensionable
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-white/5">
                    {selectedPayrollDetails.map((pkg, index) => (
                      <tr
                        key={pkg.id || index}
                        className="hover:bg-slate-50 dark:hover:bg-white/[0.02]"
                      >
                        <td className="px-4 sm:px-5 py-3 text-slate-800 dark:text-slate-200 font-medium">
                          {pkg.packageName || pkg.name || "-"}
                        </td>
                        <td className="px-4 sm:px-5 py-3 text-slate-500 dark:text-slate-400">
                          {pkg.type || "-"}
                        </td>
                        <td className="px-4 sm:px-5 py-3 text-right text-slate-700 dark:text-slate-300">
                          ETB {formatAmount(pkg.amount)}
                        </td>
                        <td className="px-4 sm:px-5 py-3 text-right">
                          {pkg.taxable ? (
                            <span className="text-emerald-600 dark:text-emerald-400">
                              Yes
                            </span>
                          ) : (
                            <span className="text-slate-400 dark:text-slate-500">
                              No
                            </span>
                          )}
                        </td>
                        <td className="px-4 sm:px-5 py-3 text-right">
                          {pkg.pensionable ? (
                            <span className="text-emerald-600 dark:text-emerald-400">
                              Yes
                            </span>
                          ) : (
                            <span className="text-slate-400 dark:text-slate-500">
                              No
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end p-4 sm:p-5 border-t border-slate-200 dark:border-white/10">
                <button
                  onClick={() => setSelectedPayrollDetails(null)}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100 dark:bg-slate-950/60 dark:border-white/10 dark:text-slate-300 dark:hover:bg-white/[0.04] transition font-semibold"
                >
                  <FaTimes />
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PayrollManagement;
